import { NumbersForm } from 'components/panels/NumbersForm';
import Button from 'components/utils/Button';
import { Icons } from 'components/utils/Icons';
import { SlidePanel } from 'components/utils/panels/AvoSlidePanel';
import { Body2, H4 } from 'components/utils/typo';
import 'css/custom.css';
import { CircleIcon } from 'lucide-react';
import { useContext, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { UIElement } from '../../../api/moduleElement';
import { GENDER, GENDER_VALUES, MODULE_TYPES } from '../../../constants';
import { useTriggers } from '../../../hooks/module/resources/useTriggers';
import { ModuleContext } from '../../utils/module/ModuleContext';
import { DraggableContext, ModuleElementContext } from '../ModuleBoard';
import { GroupCardContext } from './GroupCard';
import { CardContainer } from './utils/CardContainer';

export interface NumbersFormVariables {
  title: string;
  is_submittable: boolean;
  panel_item: {
    id: number;
    title: string;
    display_value: string;
    numeric_type: string;
    is_optional: boolean;
    default_value?: string;
    calculator?: number;
    infobox?: number;
  }[];
  trigger?: number;
  module?: number;
  calculator?: number;
  position?: number;
  container?: number;
  is_hide: boolean;
  variable: string;
}

interface NumbersCardProps {
  element: UIElement;
}

export const NumbersCard = ({ element }: NumbersCardProps) => {
  const { module } = useContext(ModuleContext);
  const { setIsDragDisabled } = useContext(DraggableContext);

  const { groupCardId, isInsideGroupCard } = useContext(GroupCardContext);
  const { refetchModuleElements, duplicateUIElement, deleteUIElement, clearModuleElements } =
    useContext(ModuleElementContext);
  const [formOpen, setFormOpen] = useState(!element.id);
  useEffect(() => setIsDragDisabled(formOpen), [formOpen]);

  const { data: triggers } = useTriggers();

  const alwayOnTrigger = triggers.find((data) => data.title === 'Always On');

  const defaultValues: NumbersFormVariables = {
    title: element.title ?? '',
    is_submittable: element.is_submittable ?? true,
    panel_item: element.panel_item ?? [],
    trigger: element.trigger ? element.trigger.id : alwayOnTrigger?.id,
    module: module?.type === MODULE_TYPES.ALGO ? module.id : undefined,
    calculator: module?.type === MODULE_TYPES.CALCULATOR ? module.id : undefined,
    container: groupCardId,
    position: element.position,
    is_hide: !!element.is_hide,
    variable: element.variable_item?.name || '',
  };

  const form = useForm<NumbersFormVariables>({ defaultValues });

  const onCancel = () => {
    clearModuleElements();
    form.reset(defaultValues);
    setFormOpen(false);
  };

  const onSave = async () => {
    try {
      await refetchModuleElements();
      setFormOpen(false);
    } catch (e) {
      console.error('Failed to save module elements:', e);
    }
  };

  return (
    <FormProvider {...form}>
      <CardContainer
        elementId={element.id}
        onOpenPanel={() => setFormOpen(true)}
        isPreviewing={formOpen}
        variables={form.watch('panel_item').map((item) => ({ type: 'Numbers', name: item.title }))}
      >
        <H4 className='text-gray-900'>{form.watch('title')}</H4>
        {form.watch('panel_item').map((item, idx) => (
          <div className='space-y-1 pt-1' key={idx}>
            <div className='flex items-center gap-1'>
              <Body2 className='text-gray-700'>
                {item.title} {item.is_optional && '(optional)'}
              </Body2>

              {item.infobox && <Icons.ToolInfobox />}
              {item.calculator && <Icons.ToolCalc />}
            </div>
            {item.display_value.includes(GENDER.TERMINOLOGY) ? (
              <div className='mb-3 flex space-x-4'>
                {Object.keys(GENDER_VALUES).map((genderKey, index) => (
                  <div
                    key={index}
                    className='flex w-1/2 items-center gap-1 rounded bg-gray-100 px-2 py-2'
                  >
                    <CircleIcon className='text-gray-300' size={16} />
                    <Body2 className='text-gray-700'>{genderKey}</Body2>
                  </div>
                ))}
              </div>
            ) : (
              <div className='h-[40px] rounded border border-gray-300' />
            )}
          </div>
        ))}
        {form.watch('is_submittable') && !isInsideGroupCard && (
          <div className='flex justify-center'>
            <div className='w-full max-w-[164px]'>
              <Button>Submit</Button>
            </div>
          </div>
        )}
      </CardContainer>
      <SlidePanel open={formOpen}>
        <NumbersForm elementId={element.id} onSave={onSave} onCancel={onCancel} />
      </SlidePanel>
    </FormProvider>
  );
};
